import { Box, makeStyles } from '@material-ui/core';
import React, { useEffect } from 'react';
import { FunctionField, List, Record, ReduxState, useLoading, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { product_types } from '../../../pages/paths';
import {
    GROUP_METADATA_AVATAR_TYPE,
    GROUP_METADATA_AVATAR_TYPES,
    MEDIA_TAG_APPROVAL,
} from '../../../providers/resources';
import {
    addToGroupMetadataAvatarTypeData,
    selectGroupMetadataAvatarType,
} from '../../../redux/actions/group.metadata.avatar.type.actions';
import { CustomReducerState } from '../../../redux/reducers';
import { tableStyles } from '../../../theme/customTheme';
import { getNameWithoutPrefix } from '../../../utils/resources/MetadataAvatarType';
import { GroupMetadataAvatarType } from '../../../utils/types';
import DataGridCustom from '../../UI/DataGrid';
import MediaCell from '../../UI/MediaCell/MediaCell';
import ProductTypeActionsFilters from './ProductTypeActionsToolbar/ProductTypeActionsFilters/ProductTypeActionsFilters';
import ProductTypeActionsToolbar from './ProductTypeActionsToolbar/ProductTypeActionsToolbar';
import '../../../styles/list.css';
import { PAGE_MAX_WIDTH } from '../../../theme/crmColors';

const styles = () => ({
    ...tableStyles(),
    pageContainer: {
        display: 'grid',
        gap: 16,
        padding: 24,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        boxSizing: 'border-box',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto auto 1fr',
        height: '100%',
        minHeight: '100%',
        '@media (max-width:600px)': {
            gap: 8,
            padding: 8,
        },
    },
});
//@ts-ignore
const useStyles = makeStyles(styles);

const ProductTypeList = () => {
    const t = useTranslate();
    const classes = useStyles();
    const dispatch = useDispatch();
    const loading = useLoading();
    const selectedGMDAT: GroupMetadataAvatarType | Record | null = useSelector(
        (state: ReduxState & CustomReducerState) => {
            const selectedGroupMetadataAvatarTypeUri = (
                state.rikutec.groupsMetadataAvatarType.selected as GroupMetadataAvatarType
            )['@id'];
            return selectedGroupMetadataAvatarTypeUri &&
                selectedGroupMetadataAvatarTypeUri in state.admin.resources[GROUP_METADATA_AVATAR_TYPES].data
                ? state.admin.resources[GROUP_METADATA_AVATAR_TYPES].data[selectedGroupMetadataAvatarTypeUri]
                : null;
        },
    );

    const listProps = {
        resource: GROUP_METADATA_AVATAR_TYPES,
        hasCreate: true,
        hasEdit: true,
        hasList: true,
        hasShow: false,
        basePath: product_types,
        sort: { field: 'createdAt', order: 'ASC' },
        perPage: 10,
        filter: {
            serialization_groups: ['group_metadata_avatar_type_files'],
        },
    };
    // noinspection JSUnusedGlobalSymbols
    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectGroupMetadataAvatarType(record));
            return null;
        },
        toFindSelected: {
            resource: 'groupsMetadataAvatarType',
            key: 'selected',
        },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };
    useEffect(() => {
        if (
            selectedGMDAT &&
            selectedGMDAT.hasOwnProperty('@id') &&
            !loading &&
            //eslint-disable-next-line
            selectedGMDAT.files !== selectedGMDAT.files
        ) {
            dispatch(selectGroupMetadataAvatarType(selectedGMDAT['@id']));
        }
    }, [loading, dispatch, selectedGMDAT]);

    return (
        <Box className={classes.pageContainer} data-testid='ProductTypeList'>
            <Box className='list'>
                <List
                    component='div'
                    {...listProps}
                    filters={<ProductTypeActionsFilters />}
                    actions={<ProductTypeActionsToolbar />}
                    bulkActionButtons={false}
                >
                    <DataGridCustom {...dataGridProps}>
                        <FunctionField
                            sortable={true}
                            sortBy='name'
                            source='name'
                            label={t('app.productType.name')}
                            render={(record: Record | undefined) => getNameWithoutPrefix(record?.['name'])}
                        />
                        <FunctionField
                            label={t('app.productType.approval')}
                            source='medias'
                            render={(record: Record | undefined) => (
                                <MediaCell
                                    record={record!}
                                    mediaTag={MEDIA_TAG_APPROVAL}
                                    objectType={GROUP_METADATA_AVATAR_TYPE}
                                    reduxUpdater={addToGroupMetadataAvatarTypeData}
                                    mediasObjectContainer='medias'
                                    storeContainer='groupsMetadataAvatarType'
                                />
                            )}
                        />
                    </DataGridCustom>
                </List>
            </Box>
        </Box>
    );
};

export default ProductTypeList;
